import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Menu, X, Music2, Facebook, Instagram, Linkedin, BookOpen, HardHat, Zap, Weight, Gauge, LandPlot, Shield, Wrench, Hammer} from 'lucide-react';
import { useApp } from '../context/AppContext';
import imagen1 from '../assets/images/HeaderFooter/inverdronjgwiconheader.webp'

//-- Proyecto diseñado, programado, testeado, ejecutado y publicado por Alex Castillo.
//-- Autor: Alex Castillo © 2026 Alex Castillo – Todos los derechos reservados.

const Header = ({ currentPage, setCurrentPage }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { language, setLanguage, t } = useApp();

  const navItems = [
    { key: 'home', label: t.nav.home },
    { 
      key: 'catalog', 
      label: t.nav.catalog,
      hasDropdown: true,
      dropdownItems: [
        { key: 'ppe', label: t.categories.ppe, icon: HardHat},
        { key: 'tct', label: t.categories.tct, icon: Zap},
        { key: 'enm', label: t.categories.enm, icon: Weight},
        { key: 'mydTools', label: t.categories.mydTools, icon: Gauge},
        { key: 'ept', label: t.categories.ept, icon: LandPlot},
        { key: 'heightWork', label: t.categories.heightWork, icon: Shield},
        { key: 'isolatedTools', label: t.categories.isolatedTools, icon: Wrench},
        { key: 'manualandmotorTools', label: t.categories.manualandmotorTools, icon: Hammer}
      ]
    },
    { key: 'about', label: t.nav.about },
    { 
      key: 'services', 
      label: t.nav.services,
      hasDropdown: true,
      dropdownItems: [
        { key: 'maintenance', label: 'Mantenimiento' },
        { key: 'laboratory', label: 'Laboratorio' },
        { key: 'consulting', label: 'Asesoramiento Técnico' }
      ]
    },
    { key: 'contact', label: t.nav.contact }
  ];

  const socialLinks = [
    { Icon: Facebook, url: 'https://www.facebook.com/profile.php?id=61578452161359', name: 'Facebook', color: 'text-blue-600' },
    { Icon: Instagram, url: 'https://www.instagram.com/inverdron_jgw/', name: 'Instagram', color: 'text-pink-400' },
    { Icon: Linkedin, url: 'https://www.linkedin.com/in/inverdron-jgw-4b54b52b4/', name: 'Linkedin', color: 'text-blue-400' },
    { Icon: Music2, url: 'https://www.tiktok.com/@inverdron.jgw', name: 'TikTok', color: 'text-gray-800' }
  ];

  return (
    <header className="bg-white shadow-xl shadow-gray-700 sticky top-0 z-50">
      <div className="flex items-center justify-between gap-4 px-4 py-2">
        <div className="flex items-center gap-4">
          {socialLinks.map((social, index) => (
            <motion.a
              key={index}
              href={social.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`${social.color} hover:scale-110 transition-transform`}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.95 }}
              title={social.name}
            >
              <social.Icon className="w-5 h-5" />
            </motion.a>
          ))}
          
          <motion.button
            onClick={() => setCurrentPage('faq')}
            className="bg-gradient-to-r from-blue-500 to-blue-800 text-white px-3 py-1 rounded-full text-sm hover:from-blue-700 hover:to-blue-800 transition-all flex items-center gap-2"
            whileHover={{ scale: 1.12 }}
            whileTap={{ scale: 0.95 }}
          >
            FAQ
          </motion.button>

          <motion.button
            onClick={() => setCurrentPage('blog')}
            className="bg-gradient-to-r from-yellow-500 to-yellow-800 text-white px-3 py-1 rounded-full text-sm hover:from-yellow-600 hover:to-yellow-700 transition-all flex items-center gap-2"
            whileHover={{ scale: 1.12 }}
            whileTap={{ scale: 0.95 }}
          >
            <BookOpen className="w-4 h-4" />
            Blog
          </motion.button>
        </div>

        <div className="flex items-center gap-4">
          <select
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            className="bg-gray-100 border border-gray-300 rounded px-2 py-1 text-sm focus:ring-2 focus:ring-blue-400"
          >
            <option value="es">🇪🇸 ES</option>
            {/* <option value="en">🇺🇸 EN</option> */}
          </select>
        </div>
      </div>

      <div className="border-t border-gray-200">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between py-2">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <img
                src={imagen1}
                alt="Inverdron JGW"
                onClick={() => setCurrentPage('home')}
                className="h-20 w-auto object-contain cursor-pointer"
              />
            </motion.div>

            <nav className="hidden lg:flex items-center gap-6">
              {navItems.map((item, index) => (
                <div key={index} className="relative group">
                  <motion.button
                    onClick={() => setCurrentPage(item.key)}
                    className={`px-4 py-2 rounded-lg font-medium transition-all ${
                      currentPage === item.key
                        ? 'bg-gradient-to-r from-blue-500 to-blue-800 text-white shadow-lg shadow-gray-700'
                        : 'text-gray-800 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-300'
                    }`}
                    whileHover={{ scale: 1.09 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    {item.label}
                  </motion.button>

                  {item.hasDropdown && (
                    <div className={`absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl py-3 px-2
                      ${item.key === 'catalog' ? 'min-w-[420px]' : 'min-w-[220px]'}
                      opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 delay-75`}>
                      <div className={`${item.key === 'catalog' ? 'grid grid-cols-2' : 'flex flex-col'}`}>
                        {item.dropdownItems.map((dropItem, dropIndex) => (
                          <button
                            key={dropIndex}
                            onClick={() => setCurrentPage(dropItem.key)}
                            className="flex items-center gap-2 w-full text-left px-4 py-2 text-gray-700 hover:text-white hover:bg-gradient-to-r hover:from-blue-500 hover:to-blue-800 transition-all"
                          >
                            {/* 👇 ICONO DE CATEGORÍA */}
                            {dropItem.icon && (
                              <dropItem.icon className="w-4 h-4 flex-shrink-0" />
                            )}

                            <span>{dropItem.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </nav>

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-300"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {isMobileMenuOpen && (
            <motion.div 
              className="lg:hidden border-t border-gray-200 py-4"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
            >
              {navItems.map((item, index) => (
                <div key={index}>
                  <button
                    onClick={() => {
                      setCurrentPage(item.key);
                      setIsMobileMenuOpen(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-gray-800 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-300"
                  >
                    {item.label}
                  </button>
                  {item.hasDropdown && item.dropdownItems.map((dropItem, dropIndex) => (
                    <button
                      key={dropIndex}
                      onClick={() => {
                        setCurrentPage(dropItem.key);
                        setIsMobileMenuOpen(false);
                      }}
                      className="block w-full text-left px-8 text-gray-800 hover:bg-gradient-to-r hover:from-gray-100 hover:to-gray-300 text-sm"
                    >
                      {dropItem.label}
                    </button>
                  ))}
                </div>
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;

//-- Proyecto diseñado, programado, testeado, ejecutado y publicado por Alex Castillo.
//-- Autor: Alex Castillo © 2026 Alex Castillo – Todos los derechos reservados.